import {
  // Description,
  Dialog,
  DialogPanel,
  // DialogTitle,
} from "@headlessui/react";

import clsx from "clsx";

import {
  // CloseIcon,
  ExpandIcon,
  PlusIcon,
  // RightArrowIcon,
} from "components/icons";
import React, {
  useState,
  useRef,
  useEffect,
  // useMemo
} from "react";
// import clsx from "clsx";

import { imageGenerationCategories } from "data/image-categories";

type IProps = {
  isOpen: boolean;
  selectedImageStyleIndex: number;
  setIsOpen: (isOpen: boolean) => void;
  onSelected: (content: string) => void;
};

type Example = {
  image: string;
  content: string;
  aspectRatio?: string;
};

export type IImageGenerationCategories = {
  name: string;
  style: string;
  prefectForShort: string;
  isExpanded: boolean;
  perfectFor: string;
  basicTemplate?: string;
  parameterizedTemplate: string;
  examples: Example[];
  image?: string;
};

export const ExampleModal: React.FC<IProps> = ({
  isOpen,
  selectedImageStyleIndex,
  setIsOpen,
  onSelected,
}) => {
  const [examples, setExamples] = useState<IImageGenerationCategories[]>(() =>
    imageGenerationCategories.map((item, i) => ({
      ...item,
      isExpanded: i === selectedImageStyleIndex, // 初始时根据 selectedImageStyleIndex 设置展开状态
    }))
  );

  const [contentHeights, setContentHeights] = useState<number[]>(
    new Array(imageGenerationCategories.length).fill(0)
  );

  // 修复类型：允许 RefObject 的 current 为 null
  const contentRefs = useRef<React.RefObject<HTMLDivElement | null>[]>(
    imageGenerationCategories.map(() =>
      React.createRef<HTMLDivElement | null>()
    )
  );

  const onChangeStatus = (index: number) => {
    setExamples((prev) =>
      prev.map((example, i) => ({
        ...example,
        isExpanded: i === index ? !example.isExpanded : false,
      }))
    );
  };

  // 当 isOpen 或 selectedImageStyleIndex 变化时，更新展开状态
  useEffect(() => {
    if (isOpen) {
      setExamples((prev) =>
        prev.map((example, i) => ({
          ...example,
          isExpanded: i === selectedImageStyleIndex,
        }))
      );
    }
  }, [isOpen, selectedImageStyleIndex]);

  // 测量高度
  useEffect(() => {
    if (!isOpen) return;

    const measureHeights = () => {
      const newHeights = examples.map((example, index) => {
        if (!example.isExpanded) return 0;
        const contentEl = contentRefs.current[index]?.current;
        return contentEl?.scrollHeight || 0;
      });

      if (JSON.stringify(newHeights) !== JSON.stringify(contentHeights)) {
        setContentHeights(newHeights);
      }
    };

    // 确保 DOM 渲染完成后测量高度
    const timeoutId = setTimeout(measureHeights, 0);
    const animationFrameId = requestAnimationFrame(measureHeights);

    const observers: ResizeObserver[] = [];
    examples.forEach((example, index) => {
      if (example.isExpanded) {
        const contentEl = contentRefs.current[index]?.current;
        if (contentEl) {
          const observer = new ResizeObserver(measureHeights);
          observer.observe(contentEl);
          observers.push(observer);
        }
      }
    });

    return () => {
      clearTimeout(timeoutId);
      cancelAnimationFrame(animationFrameId);
      observers.forEach((observer) => observer.disconnect());
    };
  }, [examples, isOpen, contentHeights]);

  return (
    <Dialog
      open={isOpen}
      onClose={() => setIsOpen(false)}
      className="relative z-50"
    >
      {/* max-w-lg */}
      <div className="fixed inset-0 flex w-screen items-center justify-center bg-black/50">
        <DialogPanel className="w-full lg:min-w-[525px] max-w-[90%] lg:max-w-fit p-3 border bg-white rounded-md">
          {/* <div className="w-full flex flex-row items-center justify-between text-[#525252] text-[16px] font-medium">
            <span>Browse Use Cases</span>
            <span
              className="flex flex-row items-center justify-center cursor-pointer"
              onClick={() => setIsOpen(false)}
            >
              <CloseIcon />
            </span>
          </div> */}
          <div className=" max-h-[calc(100vh-160px)] overflow-hidden overflow-y-auto">
            {examples.map((example, index) => (
              <div className="py-1" key={`example-${index}`}>
                <div
                  className="flex flex-row items-center gap-2 cursor-pointer py-2 lg:py-3  hover:bg-blue-50 px-2 rounded-[4px]"
                  onClick={() => onChangeStatus(index)}
                >
                  <span className="text-[#94A3B8]">
                    {example.isExpanded ? <ExpandIcon /> : <PlusIcon />}
                  </span>
                  <span className="text-[15px] lg:text-[16px] font-medium text-[#171717]">
                    {example.name}
                  </span>
                </div>

                <div
                  className={clsx(
                    "overflow-x-auto transition-all duration-500 ease-in-out flex flex-row gap-[10px] mt-2"
                    // example.isExpanded && "mb-3"
                  )}
                  style={{
                    height: example.isExpanded
                      ? `${contentHeights[index]}px`
                      : "0px",
                  }}
                  // ref={contentRefs.current[index]}
                >
                  {example.examples.map((example, key) => (
                    <div
                      key={key}
                      className="flex flex-col items-center max-w-[200px] lg:max-w-[287px] cursor-pointer group"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelected(example.content);
                      }}
                    >
                      <div className="relative">
                        <img
                          src={example.image}
                          alt="case"
                          className="w-auto h-auto max-w-[200px] lg:max-w-[287px] max-h-[287px] rounded-md object-contain border border-solid border-gray-300"
                        />
                        <div className="text-white px-4 w-full rounded-bl-md block rounded-br-md bg-[rgba(0,0,0,0.9)] absolute left-0 bottom-0 text-center h-0 group-hover:h-[40px] leading-[40px] overflow-hidden transition-[height] duration-300 ease-in-out">
                          Try this
                        </div>
                      </div>

                      <div className="group-hover:bg-[rgba(229,231,235,0.60)] ">
                        <div className="text-[14px] text-[#737373] rounded-[4px] line-clamp-4 group-hover:text-[#4338CA] m-2">
                          {example.content}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Hide measurement container (non-animated area, used to measure content height correctly) */}
                <div
                  ref={contentRefs.current[index]}
                  className="absolute opacity-0 pointer-events-none z-[-999] w-[90%] lg:w-auto"
                  aria-hidden
                >
                  <div className="flex flex-row gap-[10px] overflow-x-auto">
                    {example.examples.map((example, key) => (
                      <div
                        key={key}
                        className="flex flex-col items-center max-w-[287px]"
                      >
                        <div className="relative">
                          <img
                            src={example.image}
                            alt="case"
                            className="w-auto h-auto max-w-[200px] lg:max-w-[287px] max-h-[287px] rounded-md object-contain border border-solid border-gray-300"
                          />
                        </div>
                        <div className="text-[14px] text-[#737373] font-medium rounded-[4px] line-clamp-4 p-2">
                          {example.content}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border-b-[0.3px] border-solid border-[#94A3B8] border-opacity-30"></div>
              </div>
            ))}
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};
